/**
 * Job Hunter — the candidates, meta and the active candidate's agents every
 * page needs, cached at module level so moving between tabs (each route
 * remounts) doesn't flash empty states. The active candidate is remembered per
 * browser.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { jobHunterService, type JhAgent, type JhMeta, type JhProfile } from '@/services/job-hunter.service';

interface State {
  profiles: JhProfile[] | null;
  meta: JhMeta | null;
  activeUid: string | null;
  error: boolean;
  loading: boolean;
  /** Agents of `agentsFor` (the active candidate). */
  agents: JhAgent[] | null;
  agentsFor: string | null;
}

const KEY = 'jh.profile';
const read = () => { try { return localStorage.getItem(KEY); } catch { return null; } };
const write = (v: string | null) => { try { if (v) localStorage.setItem(KEY, v); else localStorage.removeItem(KEY); } catch { /* storage blocked */ } };

let state: State = { profiles: null, meta: null, activeUid: read(), error: false, loading: false, agents: null, agentsFor: null };
const listeners = new Set<() => void>();
const set = (patch: Partial<State>) => { state = { ...state, ...patch }; listeners.forEach((l) => l()); };
let inflight: Promise<void> | null = null;
let agentsInflight: { uid: string; p: Promise<void> } | null = null;

export function refreshJobHunter(): Promise<void> {
  if (inflight) return inflight;
  set({ loading: true });
  inflight = Promise.all([jobHunterService.profiles(), state.meta ? Promise.resolve(state.meta) : jobHunterService.meta()])
    .then(([profiles, meta]) => {
      const activeUid = profiles.some((p) => p.profileUid === state.activeUid) ? state.activeUid : profiles[0]?.profileUid ?? null;
      write(activeUid);
      set({ profiles, meta, activeUid, error: false });
    })
    .catch(() => set({ error: true }))
    .finally(() => { inflight = null; set({ loading: false }); });
  return inflight;
}

/** Re-reads the active candidate's agents (after a create / edit / run). */
export function refreshAgents(): Promise<void> {
  const uid = state.activeUid;
  if (!uid) { set({ agents: [], agentsFor: null }); return Promise.resolve(); }
  if (agentsInflight?.uid === uid) return agentsInflight.p;
  const p = jobHunterService.agents(uid)
    .then((agents) => { if (state.activeUid === uid) set({ agents, agentsFor: uid }); })
    .catch(() => { if (state.activeUid === uid) set({ agents: [], agentsFor: uid }); })
    .finally(() => { if (agentsInflight?.uid === uid) agentsInflight = null; });
  agentsInflight = { uid, p };
  return p;
}

export function setActiveProfile(uid: string) {
  if (uid === state.activeUid) return;
  write(uid);
  set({ activeUid: uid, agents: null, agentsFor: null });
}

export function upsertProfileInStore(p: JhProfile) {
  const list = state.profiles ?? [];
  const exists = list.some((x) => x.profileUid === p.profileUid);
  set({ profiles: exists ? list.map((x) => (x.profileUid === p.profileUid ? { ...x, ...p } : x)) : [...list, p] });
}

export function upsertAgentInStore(a: JhAgent) {
  if (a.profileUid !== state.agentsFor) return;
  const list = state.agents ?? [];
  set({ agents: list.some((x) => x.agentUid === a.agentUid) ? list.map((x) => (x.agentUid === a.agentUid ? { ...x, ...a } : x)) : [...list, a] });
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const snapshot = () => state;

export function useJobHunter() {
  const snap = useSyncExternalStore(subscribe, snapshot);
  useEffect(() => { if (!snap.profiles && !snap.loading && !snap.error) void refreshJobHunter(); }, [snap.profiles, snap.loading, snap.error]);
  useEffect(() => { if (snap.activeUid && snap.agentsFor !== snap.activeUid && snap.profiles) void refreshAgents(); }, [snap.activeUid, snap.agentsFor, snap.profiles]);
  const profile = snap.profiles?.find((p) => p.profileUid === snap.activeUid) ?? null;
  const reload = useCallback(() => refreshJobHunter(), []);
  const agents = snap.agentsFor === snap.activeUid ? snap.agents : null;
  return { ...snap, agents, profile, profileUid: profile?.profileUid ?? null, reload, reloadAgents: refreshAgents, setActive: setActiveProfile };
}
